import { useMemo, useState } from 'react'
import { Upload, Search, Download, ExternalLink, RotateCcw, DownloadCloud } from 'lucide-react'
import { api } from '../lib/api'
import { useAuth } from '../contexts/auth'
import { Card, CardContent } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Badge } from '../components/ui/Badge'
import { useActiveTenant } from '../contexts/tenant'

// Rastreio em lote: cola ou sobe uma planilha com envios, e cada linha volta
// com onde o pacote está e o link de rastreio. Nasceu da pergunta que o
// cliente e o SAC fazem toda semana — "e esses aqui?" — com uma lista de
// pedidos na mão e ninguém para abrir um por um.

type Linha = {
  valor: string; encontrado: boolean; casouPor: string | null; orderId: string | null
  ws: string | null; pedido: string | null; nf: string | null; cliente: string | null; canal: string | null
  status: string | null; statusLabel: string | null; statusEnvio: string | null; statusEnvioLabel: string | null
  transportadora: string | null; rastreio: string | null; link: string | null
  expedidoEm: string | null; postadoEm: string | null
  separacao: { id: string; reference: string | null; status: string; baixada: boolean; ativa: boolean } | null
  /** Já saiu do estoque (por pedido ou por onda que baixou) — não baixa de novo. */
  baixado: boolean
  /** Tem etiqueta pela regra da tela de Pedidos — sem ela não entra em onda. */
  comEtiqueta: boolean
  /** Outros pedidos com a MESMA NF (duplicata). */
  outrosComAMesmaNf?: number
}
type Resultado = {
  total: number; encontrados: number; naoEncontrados: number
  porStatus: { status: string; label: string; n: number }[]
  linhas: Linha[]
}

/** Mensagem de erro que diz o que aconteceu de verdade. "Falha ao consultar"
 *  sozinho não distingue um bug no servidor de um 502 durante o deploy — e
 *  a diferença é "avise" versus "tente de novo em um minuto". */
function descreverErro(e: unknown, fallback: string): string {
  const err = e as { response?: { status?: number; data?: { title?: string; detail?: string } }; message?: string }
  const st = err.response?.status
  const title = err.response?.data?.title
  if (title) return st ? `${title} (HTTP ${st})` : title
  if (st === 502 || st === 503 || st === 504) return `${fallback}: o servidor não respondeu (HTTP ${st}) — pode estar reiniciando após um deploy. Tente de novo em um minuto.`
  if (st) return `${fallback} (HTTP ${st})`
  if (err.message) return `${fallback}: ${err.message}`
  return fallback
}

/** Quebra o texto em linhas e colunas. Aceita ; , TAB ou uma coluna só —
 *  planilha exportada do Excel, do Bling ou copiada de um e-mail. */
function parseTabela(texto: string): string[][] {
  const linhas = texto.split(/\r?\n/).map(l => l.trim()).filter(Boolean)
  if (!linhas.length) return []
  const sep = [';', '\t', ','].find(s => linhas[0].includes(s)) ?? null
  return linhas.map(l => (sep ? l.split(sep) : [l]).map(c => c.replace(/^"|"$/g, '').trim()))
}

/** Palpite de qual coluna tem o identificador: a que mais parece código WS,
 *  rastreio ou número. O usuário pode trocar. */
/** O valor parece um código de envio (NF, WS, pedido, rastreio)? Serve para
 *  decidir se a primeira linha é cabeçalho — e o custo de errar é alto: marcada
 *  por engano, ela ENGOLE a primeira nota da lista em silêncio, e quem consultou
 *  conclui que o envio "não está no sistema". */
function pareceCodigo(v: string): boolean {
  const t = (v ?? '').trim()
  if (!t) return false
  return /^\d{3,}$/.test(t)                      // NF, nº do pedido
    || /^WS-?\d+$/i.test(t)                      // código WS
    || /^[A-Z]{2}\d{9}[A-Z]{2}$/i.test(t)        // rastreio Correios
    || /^[A-Z]{2,4}-?[A-Z0-9]{8,}$/i.test(t)      // LGI-…, ORD-…
}

/** Cabeçalho é exceção, não padrão: lista colada à mão vem sem ele. Só assume
 *  cabeçalho quando a primeira linha NÃO parece um código. */
function pareceCabecalho(tabela: string[][], col: number): boolean {
  if (tabela.length < 2) return false
  const primeira = tabela[0]?.[col] ?? ''
  return !pareceCodigo(primeira)
}

function colunaProvavel(tabela: string[][]): number {
  if (!tabela.length) return 0
  const n = Math.max(...tabela.map(r => r.length))
  let melhor = 0, melhorNota = -1
  for (let c = 0; c < n; c++) {
    const nota = tabela.reduce((s, r) => {
      const v = (r[c] ?? '').toUpperCase()
      if (/^WS-\d+/.test(v)) return s + 3
      if (/^[A-Z]{2}\d{9}[A-Z]{2}$/.test(v) || /^[A-Z]{2,3}-?[A-Z0-9]{8,}$/.test(v)) return s + 3
      if (/^\d{4,}$/.test(v)) return s + 1
      return s
    }, 0)
    if (nota > melhorNota) { melhorNota = nota; melhor = c }
  }
  return melhor
}

const corEnvio = (s: string | null): 'default' | 'info' | 'warning' | 'success' | 'danger' => {
  if (s === 'delivered') return 'success'
  if (s === 'posted') return 'info'
  if (s === 'canceled') return 'danger'
  if (!s) return 'default'
  return 'warning'
}

export function RastreioLotePage() {
  const tenantId = useActiveTenant()
  const [texto, setTexto] = useState('')
  const [coluna, setColuna] = useState<number | null>(null)
  // null = deixa o sistema decidir pela cara da primeira linha; true/false = o
  // operador mandou. Antes era `true` fixo, e uma lista de três NFs consultava
  // duas — a primeira virava "cabeçalho" sem ninguém notar.
  const [cabecalhoManual, setCabecalhoManual] = useState<boolean | null>(null)
  const [busy, setBusy] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [res, setRes] = useState<Resultado | null>(null)
  const [filtro, setFiltro] = useState<string>('')

  const tabela = useMemo(() => parseTabela(texto), [texto])
  const colunas = Math.max(0, ...tabela.map(r => r.length))
  const colEscolhida = coluna ?? colunaProvavel(tabela)
  const temCabecalho = cabecalhoManual ?? pareceCabecalho(tabela, colEscolhida)
  const cabecalho = temCabecalho && tabela.length > 1 ? tabela[0] : null
  const valores = useMemo(
    () => (cabecalho ? tabela.slice(1) : tabela).map(r => r[colEscolhida] ?? '').filter(Boolean),
    [tabela, cabecalho, colEscolhida],
  )

  async function lerArquivo(f: File) {
    // Export da Tray (e do Excel em português) vem em latin1/windows-1252. Lido
    // como UTF-8 vira "�" nos acentos — e "Endere�o" no cabeçalho atrapalha
    // quem escolhe a coluna. Se aparecer o caractere de substituição, relê
    // como windows-1252.
    const bytes = await f.arrayBuffer()
    let t = new TextDecoder('utf-8').decode(bytes)
    if (t.includes('\uFFFD')) {
      try { t = new TextDecoder('windows-1252').decode(bytes) } catch { /* fica o utf-8 */ }
    }
    setTexto(t); setColuna(null); setCabecalhoManual(null); setRes(null); setErro(null)
  }

  async function consultar() {
    if (!tenantId) { setErro('Selecione um cliente no menu lateral.'); return }
    if (!valores.length) { setErro('Nenhum código na coluna escolhida.'); return }
    setBusy(true); setErro(null); setRes(null)
    try {
      const { data } = await api.post<Resultado>('/outbound/rastreio-lote', { tenantId, valores })
      setRes(data); setFiltro('')
    } catch (e) {
      setErro(descreverErro(e, 'Falha ao consultar os envios'))
    } finally { setBusy(false) }
  }

  async function baixarCsv() {
    if (!tenantId || !valores.length) return
    try {
      const r = await api.post('/outbound/rastreio-lote', { tenantId, valores, formato: 'csv' }, { responseType: 'blob' })
      const url = URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }))
      const a = document.createElement('a')
      a.href = url; a.download = 'rastreio-em-lote.csv'
      document.body.appendChild(a); a.click(); a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 15000)
    } catch (e) {
      setErro(descreverErro(e, 'Falha ao gerar o CSV'))
    }
  }

  const linhasVisiveis = (res?.linhas ?? []).filter(l => {
    if (!filtro) return true
    if (filtro === 'nao-encontrado') return !l.encontrado
    return l.encontrado && (l.statusEnvio ?? `pedido:${l.status}`) === filtro
  })

  // ── Refazer a separação ──
  // O caso real: a loja diz "aguardando envio", o Melhor Envio diz que a etiqueta
  // nunca foi postada, e aqui o pedido consta como EXPEDIDO. O pacote não saiu.
  // A triagem é feita AQUI, sobre as linhas visíveis (o chip de situação vale
  // como filtro), e o servidor repete a mesma triagem antes de criar a onda.
  const { user } = useAuth()
  const podeReseparar = user?.role !== 'client'
  const [refOnda, setRefOnda] = useState('')
  const [reseparando, setReseparando] = useState(false)
  const triagem = useMemo(() => {
    const entram: Linha[] = []
    const postados: Linha[] = []
    const emOnda: Linha[] = []
    const cancelados: Linha[] = []
    const semEtiqueta: Linha[] = []
    for (const l of linhasVisiveis) {
      if (!l.encontrado || !l.orderId) continue
      if (l.status === 'cancelled') { cancelados.push(l); continue }
      if (l.statusEnvio === 'posted' || l.statusEnvio === 'delivered' || l.postadoEm) { postados.push(l); continue }
      if (!l.comEtiqueta) { semEtiqueta.push(l); continue }
      if (l.separacao?.ativa) { emOnda.push(l); continue }
      entram.push(l)
    }
    return {
      entram, postados, emOnda, cancelados, semEtiqueta,
      reabrir: entram.filter(l => l.status === 'shipped').length,
      jaBaixados: entram.filter(l => l.baixado).length,
    }
  }, [linhasVisiveis])

  // ── Não encontrado: buscar a NF no ERP ──
  // "Não está no sistema" quase sempre é nota que não foi importada (webhook
  // perdido, nota emitida fora da janela do sync). Ficava assim: a tela dizia
  // "não encontrado" e o caminho para resolver era outra tela, com outro nome,
  // que ninguém associava ao problema. Aqui o conserto fica ao lado do sintoma.
  const naoEncontradas = linhasVisiveis
    .filter(l => !l.encontrado && /^\d{1,9}$/.test(l.valor.trim()))
    .map(l => l.valor.trim())
  const [importando, setImportando] = useState(false)
  const [importMsg, setImportMsg] = useState<string | null>(null)

  async function importarDoErp() {
    if (!tenantId || !naoEncontradas.length) return
    if (!window.confirm(`Buscar ${naoEncontradas.length} nota(s) no ERP e importar para o WsFull?\n\n`
      + `${naoEncontradas.slice(0, 10).join(', ')}${naoEncontradas.length > 10 ? '…' : ''}\n\n`
      + 'Uma consulta por nota (limite do Bling), pode levar alguns segundos cada.')) return
    setImportando(true); setImportMsg(null); setErro(null)
    try {
      const { data } = await api.post<{
        requested: number; imported: number; ignored: number; failed: number
        notFound: string[]; errors: string[]
        // Sem provider: o servidor resolve o ERP ativo deste cliente.
      }>('/integrations/import-nfs', { tenantId, nfs: naoEncontradas })
      const partes = [`${data.imported} importada(s)`]
      if (data.ignored) partes.push(`${data.ignored} ignorada(s) pelo filtro de destinatário`)
      if (data.notFound.length) partes.push(`${data.notFound.length} não existe(m) no ERP: ${data.notFound.slice(0, 8).join(', ')}`)
      if (data.failed) partes.push(`${data.failed} falha(s): ${data.errors.slice(0, 3).join(' | ')}`)
      setImportMsg(partes.join(' · '))
      if (data.imported) await consultar()
    } catch (e) {
      setImportMsg(descreverErro(e, 'Falha ao importar as notas do ERP'))
    } finally { setImportando(false) }
  }

  async function reseparar() {
    if (!tenantId || !triagem.entram.length) return
    const n = triagem.entram.length
    const msg = [
      `Criar uma onda nova com ${n} pedido(s)?`,
      triagem.reabrir ? `• ${triagem.reabrir} constam como EXPEDIDOS e serão reabertos (voltam para a fila).` : null,
      triagem.jaBaixados ? `• ${triagem.jaBaixados} já saíram do estoque antes — NÃO baixam de novo na conclusão desta onda.` : null,
      (triagem.postados.length + triagem.emOnda.length + triagem.cancelados.length + triagem.semEtiqueta.length)
        ? `• Ficam de fora: ${triagem.semEtiqueta.length} sem etiqueta, ${triagem.postados.length} já postado(s), ${triagem.emOnda.length} em onda ativa, ${triagem.cancelados.length} cancelado(s).` : null,
    ].filter(Boolean).join('\n')
    if (!window.confirm(msg)) return
    setReseparando(true); setErro(null)
    try {
      const { data } = await api.post<{
        onda: { id: string; reference: string; orders: number } | null
        reabertos: number; jaBaixados: number
        pulados: { orderId: string; pedido: string | null; motivo: string }[]
      }>('/outbound/rastreio-lote/reseparar', {
        tenantId, orderIds: triagem.entram.map(l => l.orderId), reference: refOnda.trim() || undefined,
      })
      const pul = data.pulados.length
        ? `\n\nFicaram de fora (${data.pulados.length}): ${data.pulados.slice(0, 12).map(p => `${p.pedido ?? p.orderId.slice(0, 8)} — ${p.motivo}`).join('; ')}${data.pulados.length > 12 ? '…' : ''}`
        : ''
      if (data.onda) {
        alert(`Onda ${data.onda.reference} criada com ${data.onda.orders} pedido(s) — já aparece em Separações.\n` +
          `${data.reabertos} reaberto(s); ${data.jaBaixados} já tinham saído do estoque e não baixam de novo.${pul}`)
      } else {
        alert(`Nenhum pedido entrou na onda.${pul}`)
      }
      setRefOnda('')
      await consultar()
    } catch (e) {
      setErro(descreverErro(e, 'Falha ao refazer a separação'))
    } finally { setReseparando(false) }
  }

  if (!tenantId) {
    return <div className="flex h-64 items-center justify-center text-sm text-gray-500">Selecione um cliente no menu lateral para continuar.</div>
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-display text-xl text-gray-900">Rastreio em lote</h2>
        <p className="text-sm text-gray-500">
          Cole ou suba uma planilha com envios. Cada linha volta com a situação e o link de rastreio.
          Vale código WS, número da NF, número do pedido, nº no ERP ou código de rastreio — o sistema descobre qual é.
        </p>
      </div>

      <Card>
        <CardContent className="space-y-3 py-4">
          <div className="flex flex-wrap items-center gap-3">
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
              <Upload className="h-4 w-4" /> Subir CSV / TXT
              <input type="file" accept=".csv,.txt,.tsv" className="hidden"
                onChange={e => { const f = e.target.files?.[0]; if (f) lerArquivo(f); e.currentTarget.value = '' }} />
            </label>
            <span className="text-xs text-gray-400">ou cole abaixo — uma linha por envio, ou a planilha inteira</span>
          </div>
          <textarea
            value={texto}
            onChange={e => { setTexto(e.target.value); setColuna(null); setCabecalhoManual(null); setRes(null) }}
            placeholder={'WS-021874\nWS-021875\n088336\nLGI-ME262CFYV66BR'}
            rows={6}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 font-mono text-sm"
          />

          {tabela.length > 0 && (
            <div className="flex flex-wrap items-center gap-4 text-sm">
              {colunas > 1 && (
                <label className="flex items-center gap-2 text-gray-600">
                  Coluna com o código:
                  <select className="rounded-lg border border-gray-200 px-2 py-1 text-sm" value={colEscolhida}
                    onChange={e => { setColuna(Number(e.target.value)); setRes(null) }}>
                    {Array.from({ length: colunas }, (_, i) => (
                      <option key={i} value={i}>
                        {cabecalho?.[i] ? `${i + 1} — ${cabecalho[i]}` : `coluna ${i + 1}`}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <label className="flex items-center gap-1 text-gray-600">
                <input type="checkbox" checked={temCabecalho}
                  onChange={e => { setCabecalhoManual(e.target.checked); setRes(null) }} />
                primeira linha é cabeçalho
              </label>
              <span className="text-gray-400">{valores.length.toLocaleString('pt-BR')} código(s) prontos</span>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={consultar} loading={busy} disabled={!valores.length}>
              <Search className="h-4 w-4" /> Consultar {valores.length ? `(${valores.length})` : ''}
            </Button>
            {res && (
              <Button variant="secondary" onClick={baixarCsv}>
                <Download className="h-4 w-4" /> Baixar CSV com status e link
              </Button>
            )}
          </div>
          {erro && <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{erro}</p>}
        </CardContent>
      </Card>

      {res && (
        <>
          {/* Resumo clicável: cada situação filtra a tabela. */}
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setFiltro('')}
              className={`rounded-lg border px-3 py-2 text-left text-xs ${!filtro ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
              <span className="block text-lg font-bold text-gray-900">{res.total}</span>
              <span className="text-gray-600">Todos</span>
            </button>
            {res.porStatus.map(s => (
              <button key={s.status} onClick={() => setFiltro(s.status)}
                className={`rounded-lg border px-3 py-2 text-left text-xs ${filtro === s.status ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                <span className={`block text-lg font-bold ${s.status === 'nao-encontrado' ? 'text-red-600' : 'text-gray-900'}`}>{s.n}</span>
                <span className="text-gray-600">{s.label}</span>
              </button>
            ))}
          </div>

          {podeReseparar && !!naoEncontradas.length && (
            <Card>
              <CardContent className="space-y-2 py-4">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="text-sm">
                    <span className="font-medium text-gray-900">Não encontrado no WsFull</span>
                    <span className="ml-2 text-gray-500">
                      {naoEncontradas.length} nota(s) da lista não existem aqui — normalmente é nota que não foi
                      importada do ERP (webhook perdido ou emitida fora da janela do sync).
                    </span>
                  </div>
                  <Button variant="secondary" onClick={importarDoErp} loading={importando}>
                    <DownloadCloud className="h-4 w-4" /> Buscar {naoEncontradas.length} no ERP
                  </Button>
                </div>
                {importMsg && <p className="text-xs text-gray-700">{importMsg}</p>}
              </CardContent>
            </Card>
          )}

          {podeReseparar && (
            <Card>
              <CardContent className="space-y-2 py-4">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="text-sm">
                    <span className="font-medium text-gray-900">Refazer a separação</span>
                    <span className="ml-2 text-gray-500">
                      {triagem.entram.length} pedido(s) da lista visível entram numa onda nova
                      {filtro ? ' (o chip selecionado acima vale como filtro)' : ''}.
                    </span>
                  </div>
                  <input
                    value={refOnda}
                    onChange={e => setRefOnda(e.target.value)}
                    placeholder="referência da onda (opcional)"
                    className="rounded-lg border border-gray-200 px-2 py-1 text-sm"
                  />
                  <Button onClick={reseparar} loading={reseparando} disabled={!triagem.entram.length}>
                    <RotateCcw className="h-4 w-4" /> Criar onda com {triagem.entram.length}
                  </Button>
                </div>
                <ul className="text-xs text-gray-600">
                  {triagem.reabrir > 0 && <li>• {triagem.reabrir} constam como <b>expedidos</b> sem postagem no Melhor Envio — serão reabertos.</li>}
                  {triagem.jaBaixados > 0 && <li>• {triagem.jaBaixados} já saíram do estoque numa onda anterior — <b>não baixam de novo</b> ao concluir.</li>}
                  {triagem.semEtiqueta.length > 0 && <li>• {triagem.semEtiqueta.length} ficam de fora: <b>sem etiqueta</b> (anexe ou sincronize o Melhor Envio) — pedido sem etiqueta não entra em onda.</li>}
                  {triagem.postados.length > 0 && <li>• {triagem.postados.length} ficam de fora: já postados/entregues no Melhor Envio.</li>}
                  {triagem.emOnda.length > 0 && <li>• {triagem.emOnda.length} ficam de fora: já estão numa onda ativa (tire de lá ou estorne antes).</li>}
                  {triagem.cancelados.length > 0 && <li>• {triagem.cancelados.length} ficam de fora: cancelados.</li>}
                </ul>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-gray-100 bg-gray-50 text-xs text-gray-500">
                    <tr>
                      <th className="px-3 py-2 text-left">Informado</th>
                      <th className="px-3 py-2 text-left">Pedido</th>
                      <th className="px-3 py-2 text-left">NF</th>
                      <th className="px-3 py-2 text-left">Cliente</th>
                      <th className="px-3 py-2 text-left">Pedido (WsFull)</th>
                      <th className="px-3 py-2 text-left">Separação</th>
                      <th className="px-3 py-2 text-left">Envio</th>
                      <th className="px-3 py-2 text-left">Transportadora</th>
                      <th className="px-3 py-2 text-left">Rastreio</th>
                      <th className="px-3 py-2 text-left">Postado</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {linhasVisiveis.map((l, i) => (
                      <tr key={i} className={l.encontrado ? '' : 'bg-red-50/40'}>
                        <td className="px-3 py-2 font-mono text-xs text-gray-700">
                          {l.valor}
                          {l.casouPor && <span className="ml-1 text-[10px] text-gray-400">({l.casouPor})</span>}
                        </td>
                        {l.encontrado ? (
                          <>
                            <td className="px-3 py-2 font-mono text-xs">{l.ws ?? l.pedido ?? '—'}</td>
                            <td className="px-3 py-2 font-mono text-xs">
                              {l.nf ?? '—'}
                              {!!l.outrosComAMesmaNf && (
                                <span className="ml-1 text-[10px] text-amber-600"
                                  title={`Existe(m) ${l.outrosComAMesmaNf} outro(s) pedido(s) com esta mesma NF — provável duplicata`}>
                                  +{l.outrosComAMesmaNf}
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-gray-700">{l.cliente ?? '—'}</td>
                            <td className="px-3 py-2"><Badge>{l.statusLabel}</Badge></td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              {l.separacao
                                ? <span className="font-mono text-xs text-gray-700" title={`onda ${l.separacao.status}`}>
                                    {l.separacao.reference ?? l.separacao.id.slice(0, 8)}
                                    {l.separacao.status === 'cancelled' && <span className="ml-1 text-[10px] text-gray-400">(estornada)</span>}
                                  </span>
                                : <span className="text-xs text-gray-400">—</span>}
                              {l.baixado && <span className="ml-1 text-[10px] text-emerald-700" title="Já saiu do estoque — não baixa de novo">· baixado</span>}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              {l.statusEnvio
                                ? <Badge variant={corEnvio(l.statusEnvio)}>{l.statusEnvioLabel}</Badge>
                                : <span className="text-xs text-gray-400">sem envio</span>}
                              {!l.comEtiqueta && <span className="ml-1 text-[10px] text-red-600" title="Sem etiqueta pela regra da tela de Pedidos — não entra em onda">· sem etiqueta</span>}
                            </td>
                            <td className="px-3 py-2 text-gray-600">{l.transportadora ?? '—'}</td>
                            <td className="px-3 py-2">
                              {l.rastreio && l.link
                                ? <a href={l.link} target="_blank" rel="noreferrer"
                                    className="inline-flex items-center gap-1 font-mono text-xs text-blue-600 hover:underline">
                                    {l.rastreio} <ExternalLink className="h-3 w-3" />
                                  </a>
                                : <span className="text-xs text-gray-400">—</span>}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-600">
                              {l.postadoEm ? new Date(l.postadoEm).toLocaleDateString('pt-BR') : '—'}
                            </td>
                          </>
                        ) : (
                          <td colSpan={9} className="px-3 py-2 text-xs text-red-700">
                            Não encontrado — não bate com código WS, NF, pedido, nº no ERP nem rastreio deste cliente.
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}
